'use client';

import { useCallback, useState } from 'react';
import { useAttachmentUpload } from '@/components/studio/editor/hooks/useAttachmentUpload';

export interface CannyImage {
  id: string;
  name: string;
  url: string;
}

export function isImageFile(file: File): boolean {
  return file.type.startsWith('image/');
}

/**
 * Картинки, приложенные к сообщению.
 *
 * Отдельно от документов: документ уходит в RAG и живёт у бренда постоянно, а
 * картинка — это «хочу похожий пост», она относится к одной реплике и в базу
 * знаний ей не место.
 */
export function useCannyImages() {
  const { uploadImage } = useAttachmentUpload();
  const [images, setImages] = useState<CannyImage[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const add = useCallback(
    async (file: File) => {
      setError(null);
      setBusy(true);
      try {
        const url = await uploadImage(file);
        setImages((current) => [...current, { id: url, name: file.name, url }]);
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : 'не удалось загрузить картинку');
      } finally {
        setBusy(false);
      }
    },
    [uploadImage],
  );

  const remove = useCallback((id: string) => {
    setImages((current) => current.filter((image) => image.id !== id));
  }, []);

  /** После отправки список чистится: картинки относятся к одному сообщению. */
  const clear = useCallback(() => setImages([]), []);

  return { images, add, remove, clear, busy, error };
}
